/**
 * VHS block — wires the Canvas UI effect into the kit's markup.
 *
 * The library itself lives in ./VHSVanilla.ts, installed from
 * https://canvasui.dev/docs/components/vhs and kept close to upstream.
 * Everything here is project-side: capability probing, the block's DOM, and
 * the painted layer used where live capture is unavailable.
 */

import { createVHS, supportsHtmlInCanvas } from './VHSVanilla';

/** How long the HTML-in-Canvas probe waits for a paint before giving up. */
const probeTimeout = 300;

/** How long to wait for the first live capture before distrusting the API. */
const captureTimeout = 700;

/** Option names the block accepts through `data-vhs-*`, with their defaults. */
const optionNames = [
  'speed',
  'wave',
  'jitter',
  'crease',
  'switching',
  'switchingHeight',
  'bloom',
  'aberration',
  'acBeat',
  'grain',
  'scanlines',
  'vignette',
  'barrel',
  'saturation',
  'exposure'
];

/**
 * Checks that the browser not only exposes the HTML-in-Canvas API but actually
 * paints through it. Runs on a throwaway canvas, so the real block is left
 * alone until the answer is known — an API that never paints would otherwise
 * hide the content behind an empty canvas.
 *
 * @returns {Promise<boolean>}
 */
function probeHtmlInCanvas() {
  if (!supportsHtmlInCanvas()) return Promise.resolve(false);

  return new Promise((resolve) => {
    const probe = document.createElement('canvas');

    probe.setAttribute('layoutsubtree', 'true');
    probe.width = 8;
    probe.height = 8;
    probe.style.cssText =
      'position:fixed;inset-block-start:-64px;inset-inline-start:-64px;inline-size:8px;block-size:8px;opacity:0;pointer-events:none';

    const sample = document.createElement('div');

    sample.style.cssText = 'inline-size:8px;block-size:8px;background:#fff';
    probe.append(sample);
    document.body.append(probe);

    const ctx = probe.getContext('2d');

    let settled = false;

    function finish(supported) {
      if (settled) return;

      settled = true;
      probe.onpaint = null;
      probe.remove();
      resolve(supported);
    }

    probe.onpaint = () => {
      try {
        ctx.reset();
        ctx.drawElementImage(sample, 0, 0);
        finish(true);
      } catch {
        finish(false);
      }
    };

    probe.requestPaint();
    setTimeout(() => finish(false), probeTimeout);
  });
}

// ─── Layer painting ───────────────────────────────────────────────────────────

/**
 * Paints the block's own layer into a canvas the shader can read.
 *
 * Snapshotting the DOM through an SVG `foreignObject` is not an option here:
 * a canvas that has had such an image drawn into it is tainted, and both
 * `getImageData` and `texImage2D` then throw. So the layer is drawn directly —
 * background image, element backgrounds, then text — using each node's own
 * computed styles and geometry.
 */

/** Draws an image over the whole area the way `object-fit: cover` would. */
function drawCover(ctx, image, width, height) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;

  ctx.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight);
}

/** Waits for an image element that may still be loading. */
function whenDecoded(image) {
  if (!image) return Promise.resolve(null);
  if (image.complete && image.naturalWidth) return Promise.resolve(image);

  return new Promise((resolve) => {
    image.addEventListener('load', () => resolve(image), { once: true });
    image.addEventListener('error', () => resolve(null), { once: true });
  });
}

/** A colour worth painting — anything fully transparent is skipped. */
function isPaintable(color) {
  return Boolean(color) && color !== 'transparent' && !/,\s*0\)$/.test(color);
}

/** True when the element's own children are text, so it can be drawn as text. */
function isTextLeaf(element) {
  return (
    element.childNodes.length > 0 &&
    [...element.childNodes].every((node) => node.nodeType === Node.TEXT_NODE) &&
    element.textContent.trim().length > 0
  );
}

/** Splits text into lines that fit the given width. */
function wrapLines(ctx, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];

  let line = '';

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;

    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;

      return;
    }

    line = candidate;
  });

  if (line) lines.push(line);

  return lines;
}

/** Applies `text-transform`, which canvas text has no notion of. */
function transformText(text, transform) {
  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  if (transform !== 'capitalize') return text;

  return text.replace(/\p{L}[\p{L}\p{M}']*/gu, (word) => word[0].toUpperCase() + word.slice(1));
}

/** Reads a numeric custom property off an element, in CSS pixels. */
function readLength(style, name) {
  const value = Number.parseFloat(style.getPropertyValue(name));

  return Number.isNaN(value) ? 0 : value;
}

/**
 * Draws one element's text using its own font, colour and alignment.
 *
 * Two block-level properties extend what plain canvas text can do, since
 * `text-shadow` has no canvas equivalent:
 *
 * - `--vhs-text-ghost` offsets a cyan and a red copy behind the text, the
 *   channel split a tape head leaves behind.
 * - `--vhs-text-glow` blooms the text the way an overdriven CRT would.
 */
function paintText(ctx, element, style, box) {
  const fontSize = Number.parseFloat(style.fontSize) || 16;
  const lineHeight = Number.parseFloat(style.lineHeight) || fontSize * 1.2;

  ctx.font = `${style.fontStyle} ${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
  ctx.fillStyle = style.color;
  ctx.textBaseline = 'middle';

  const letterSpacing = Number.parseFloat(style.letterSpacing);

  if (!Number.isNaN(letterSpacing) && 'letterSpacing' in ctx) {
    ctx.letterSpacing = `${letterSpacing}px`;
  }

  const paddingInlineStart = Number.parseFloat(style.paddingInlineStart) || 0;
  const paddingInlineEnd = Number.parseFloat(style.paddingInlineEnd) || 0;
  const innerWidth = Math.max(1, box.width - paddingInlineStart - paddingInlineEnd);
  const text = transformText(element.textContent.trim(), style.textTransform);
  const lines = wrapLines(ctx, text, innerWidth);
  const blockHeight = lines.length * lineHeight;

  let top = box.top + (box.height - blockHeight) / 2;

  if (style.display.includes('inline') || box.height > blockHeight * 2) {
    top = box.top + (box.height - blockHeight) / 2;
  }

  ctx.textAlign = style.textAlign === 'start' ? 'left' : style.textAlign;

  const left = box.left + paddingInlineStart;
  const anchor = {
    center: left + innerWidth / 2,
    right: left + innerWidth,
    end: left + innerWidth
  }[ctx.textAlign] ?? left;

  const ghost = readLength(style, '--vhs-text-ghost');
  const glow = readLength(style, '--vhs-text-glow');

  lines.forEach((line, index) => {
    const baseline = top + (index + 0.5) * lineHeight;

    if (ghost > 0) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = 'rgba(0, 190, 255, 0.55)';
      ctx.fillText(line, anchor - ghost, baseline);
      ctx.fillStyle = 'rgba(255, 40, 60, 0.55)';
      ctx.fillText(line, anchor + ghost, baseline);
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = style.color;
    }

    if (glow > 0) {
      ctx.shadowColor = style.color;
      ctx.shadowBlur = glow;
    }

    ctx.fillText(line, anchor, baseline);

    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
  });

  ctx.letterSpacing = '0px';
}

/** Draws an element's own background, including a full-bleed `::before`. */
function paintBackground(ctx, element, style, box) {
  if (isPaintable(style.backgroundColor)) {
    ctx.fillStyle = style.backgroundColor;
    ctx.fillRect(box.left, box.top, box.width, box.height);
  }

  const before = getComputedStyle(element, '::before');

  if (before.content !== 'none' && isPaintable(before.backgroundColor)) {
    ctx.fillStyle = before.backgroundColor;
    ctx.fillRect(box.left, box.top, box.width, box.height);
  }
}

/** Walks the content subtree in document order, painting each element. */
function paintTree(ctx, root, origin) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  const elements = [root];

  let node = walker.nextNode();

  while (node) {
    elements.push(node);
    node = walker.nextNode();
  }

  elements.forEach((element) => {
    if (element.classList.contains('vhs__image')) return;

    const style = getComputedStyle(element);

    if (style.display === 'none' || style.visibility === 'hidden') return;

    const rect = element.getBoundingClientRect();
    const box = {
      left: rect.left - origin.left,
      top: rect.top - origin.top,
      width: rect.width,
      height: rect.height
    };

    if (!box.width || !box.height) return;

    paintBackground(ctx, element, style, box);

    if (isTextLeaf(element)) paintText(ctx, element, style, box);
  });
}

/**
 * Paints one frame of the block into the offscreen canvas that feeds the
 * shader. Returns false when there was nothing to draw.
 */
async function paintLayer(element, image, content, canvas) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const origin = element.getBoundingClientRect();

  if (!origin.width || !origin.height) return false;

  // Canvas text ignores `font-display`, so a layer painted before the webfonts
  // land would be drawn in the fallback face and then never corrected.
  const [background] = await Promise.all([whenDecoded(image), document.fonts?.ready]);

  canvas.width = Math.max(1, Math.round(origin.width * dpr));
  canvas.height = Math.max(1, Math.round(origin.height * dpr));

  const ctx = canvas.getContext('2d');

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, origin.width, origin.height);

  if (background) drawCover(ctx, background, origin.width, origin.height);

  paintTree(ctx, content, origin);

  return true;
}

// ─── Block wiring ─────────────────────────────────────────────────────────────

const instances = new WeakMap();

/**
 * Reads options off the element, turning `data-vhs-switching-height="0.05"`
 * into `{ switchingHeight: 0.05 }`. Unknown keys are ignored.
 */
function readOptions(element) {
  const options = {};

  Object.entries(element.dataset).forEach(([key, value]) => {
    if (!key.startsWith('vhs') || key === 'vhs') return;

    const name = key.slice(3, 4).toLowerCase() + key.slice(4);

    if (!optionNames.includes(name)) return;

    const parsed = Number.parseFloat(value);

    if (!Number.isNaN(parsed)) options[name] = parsed;
  });

  return options;
}

/** Puts the block back exactly as it was authored. */
function teardown(element, content, source, output) {
  element.classList.remove('is-native', 'is-texture');

  if (content.parentElement === source) element.insertBefore(content, output);

  source.remove();
  output.remove();
  instances.delete(element);
}

/**
 * Wraps a block's existing markup in the canvas trio the effect needs.
 *
 * With HTML-in-Canvas the live markup is captured directly. Without it the
 * block paints its own layer into an offscreen canvas and the DOM copy is
 * faded out, so what the viewer sees is the whole layer through the shader
 * rather than clean markup sitting on top of it.
 *
 * @param {HTMLElement} element The block root.
 * @param {boolean} [allowNative] Set false to skip the live-capture path after
 *        it has been tried once and produced nothing.
 */
async function initVHS(element, allowNative = true) {
  if (instances.has(element)) return;

  const content = element.querySelector('.vhs__content');

  if (!content) return;

  const native = allowNative && (await probeHtmlInCanvas());
  const image = element.querySelector('.vhs__image');
  const texture = native ? null : document.createElement('canvas');

  const source = document.createElement('canvas');

  source.className = 'vhs__source';
  source.setAttribute('layoutsubtree', 'true');

  const output = document.createElement('canvas');

  output.className = 'vhs__output';
  output.setAttribute('aria-hidden', 'true');

  element.classList.toggle('is-native', native);
  element.prepend(source);

  if (native) source.append(content);

  element.append(output);

  const instance = createVHS({ source, content, output, texture }, readOptions(element));

  if (!instance) {
    teardown(element, content, source, output);

    return;
  }

  instances.set(element, instance);

  // The API can be present and still never paint, which would hide the content
  // behind an empty canvas. Give it a moment, then switch to painting our own.
  if (native) {
    setTimeout(() => {
      if (instance.hasCaptured() || !instances.has(element)) return;

      instance.destroy();
      teardown(element, content, source, output);
      initVHS(element, false);
    }, captureTimeout);

    return;
  }

  let repainting = false;

  /**
   * Repaints the layer, then reveals it. The class is only added once a frame
   * exists, so a failed snapshot leaves the plain markup on screen.
   */
  async function repaint() {
    if (repainting) return;

    repainting = true;

    try {
      const painted = await paintLayer(element, image, content, texture);

      if (!painted) return;

      element.classList.add('is-texture');
      instance.invalidate();
    } finally {
      repainting = false;
    }
  }

  repaint();

  let repaintFrame = 0;

  const repaintObserver = new ResizeObserver(() => {
    cancelAnimationFrame(repaintFrame);
    repaintFrame = requestAnimationFrame(repaint);
  });

  repaintObserver.observe(element);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-vhs]').forEach(initVHS);
});
